import { apiClient } from './api-client'

/**
 * Absolutize a tenant asset URL for use in an embedded widget.
 *
 * Uploaded assets (class-type photos, room/season/treatment images, instructor
 * portraits, blog covers, …) are stored as ROOT-RELATIVE paths, e.g.
 * `/api/uploads/tenants/<id>/…webp`. Rendered verbatim inside an `<img src>`,
 * the browser resolves them against the EMBEDDING page's origin — so on a
 * studio's own marketing site (e.g. `www.example.com/booking`) the image
 * request goes to `www.example.com/api/uploads/…`, which does not exist there,
 * and the picture is silently blank. It only shows on the studio's own
 * Holdstyring pages, where the relative path happens to resolve to the right
 * host. That is why embedded widgets appeared to be "missing images".
 *
 * Prefixing with the host the widget actually talks to (`apiClient.getBaseUrl`,
 * the `base-url` attribute / tenant origin that already served the data and the
 * uploads) makes the URL absolute so the image loads from the same place off-
 * site as on-site.
 *
 * Only leading-slash relative paths are rewritten. Absolute URLs (`http(s)://`,
 * protocol-relative `//`, `data:`/`blob:`) and empty values pass through
 * unchanged, so externally hosted assets are never touched.
 */
export function assetUrl(url: string | null | undefined): string {
  if (!url) return ''
  // Absolute or non-http scheme (http(s)://, //cdn, data:, blob:) → leave as-is.
  if (/^([a-z][a-z0-9+.-]*:|\/\/)/i.test(url)) return url
  // Only rewrite root-relative asset paths; anything else is returned verbatim.
  if (!url.startsWith('/')) return url
  const base = apiClient.getBaseUrl().replace(/\/$/, '')
  return base ? `${base}${url}` : url
}
